import React from "react";
import {
  Box,
  CardMedia,
  Chip,
  Stack,
  Dialog,
  IconButton,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SpaIcon from "@mui/icons-material/Spa";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";

const ProductImageGallery = ({
  images = [],
  productName = "",
  dietaryTags = [],
  showTags = false,
  thumbnailPosition = "right",
  selectedIndex,
  defaultIndex,
  onIndexChange = () => {},
  fullScreenOpen,
  setFullScreenOpen,
  mainImageStyles = {},
  thumbnailStyles = {},
  thumbnailContainerStyles = {},
}) => {
  const activeIndex =
    typeof selectedIndex === "number"
      ? selectedIndex
      : typeof defaultIndex === "number"
      ? defaultIndex
      : 0;

  const handleThumbnailClick = (index) => {
    onIndexChange(index);
  };

  const goLeft = () => {
    onIndexChange((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  };

  const goRight = () => {
    onIndexChange((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  };

  const ThumbnailStrip = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        alignItems: "flex-start",
        pr: 1,
        maxHeight: "300px",
        maxWidth: "100%",
        overflowY: "auto",
        ...thumbnailContainerStyles,
      }}>
      {images.map((img, index) => (
        <CardMedia
          key={index}
          component="img"
          image={img || "/images/placeholder.jpg"}
          alt={`Thumb ${index}`}
          sx={{
            cursor: "pointer",
            borderRadius: "8px",
            objectFit: "cover",
            height: 60,
            width: 60,
            border:
              index === activeIndex ? "2px solid #FD7762" : "1px solid #ddd",
            ...thumbnailStyles, // ✅ applied per-thumbnail
          }}
          onClick={() => handleThumbnailClick(index)}
        />
      ))}
    </Box>
  );

  const MainImage = (
    <Box sx={{ position: "relative", flex: 1 }}>
      <CardMedia
        component="img"
        image={images[activeIndex] || "/images/placeholder.jpg"}
        alt={productName}
        sx={{
          borderRadius: "12px",
          cursor: "pointer",
          objectFit: "cover",
          width: "100%",
          maxHeight: 300,
          ...mainImageStyles,
        }}
        onClick={() => setFullScreenOpen(true)}
      />

      {showTags && dietaryTags.length > 0 && (
        <Stack
          direction="column"
          spacing={1}
          sx={{
            position: "absolute",
            top: 12,
            left: 12,
            zIndex: 2,
          }}>
          {dietaryTags.map((tag, index) => (
            <Chip
              key={tag._id || tag.label || index}
              icon={
                <SpaIcon
                  sx={{
                    color: "white !important",
                    fontSize: 18,
                    ml: "4px",
                  }}
                />
              }
              label={tag.label}
              size="small"
              variant="outlined"
              sx={{
                bgcolor: "#4caf50",
                borderColor: "#4caf50",
                color: "#fff",
                fontWeight: 500,
                px: 1,
                py: 1,
              }}
            />
          ))}
        </Stack>
      )}
    </Box>
  );

  return (
    <>
      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        {thumbnailPosition === "left" && ThumbnailStrip}
        {MainImage}
        {thumbnailPosition === "right" && ThumbnailStrip}
      </Box>

      <Dialog
        open={fullScreenOpen}
        onClose={() => setFullScreenOpen(false)}
        maxWidth="md"
        fullWidth>
        <Box sx={{ position: "relative", p: 2, textAlign: "center" }}>
          <IconButton
            onClick={() => setFullScreenOpen(false)}
            sx={{ position: "absolute", top: 8, right: 8 }}>
            <CloseIcon />
          </IconButton>

          <Typography variant="h6" sx={{ mb: 2 }}>
            {productName}
          </Typography>

          <Box
            sx={{
              position: "relative",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}>
            <IconButton
              onClick={goLeft}
              sx={{ position: "absolute", left: 0, zIndex: 2 }}>
              <ArrowBackIosNewIcon />
            </IconButton>

            <Box
              component="img"
              src={images[activeIndex]}
              alt={`gallery image ${activeIndex}`}
              loading="lazy"
              sx={{
                width: "100%",
                maxHeight: "600px",
                objectFit: "contain",
                borderRadius: 2,
              }}
            />

            <IconButton
              onClick={goRight}
              sx={{ position: "absolute", right: 0, zIndex: 2 }}>
              <ArrowForwardIosIcon />
            </IconButton>
          </Box>
        </Box>
      </Dialog>
    </>
  );
};

export default ProductImageGallery;
